import { useEffect } from 'react';

import {
	type RouterContext,
	type RouteResource,
	useResource,
	type UseResourceHookResponse,
} from 'react-resource-router';

import { isServer } from '@atlassian/eoc-common';
import { EocError, isNetworkError, useReportError } from '@atlassian/eoc-global-context';

const useEocResource = <T extends unknown>(
	routeResource: RouteResource<T>,
	options?: {
		routerContext?: RouterContext;
	},
): UseResourceHookResponse<T> => {
	const reporter = useReportError();

	const resource = useResource(routeResource, options);
	const { error } = resource;

	useEffect(() => {
		if (!error) {
			return;
		}

		const id = routeResource.type;

		if (isServer() || isNetworkError(error)) {
			reporter(id, error);
		} else {
			throw new EocError(id, error);
		}
	}, [error, reporter, routeResource.type]);

	// User always responsible for rendering an appropriate error view on jsx.
	// Thus, we return RouteResource at the end.
	// Also, it is default behaviour if there is no error.
	return resource;
};

export default useEocResource;
